import { useQuery } from "@tanstack/react-query";
import { validateBatchProducts } from "../services/validationService";
import type {
	ValidateBatchProductsResponseDto,
	ProductDto,
	ValidationStatus,
	RuleResult,
} from "../types/validation";
import { VALIDATION_STATUS } from "../types/validation";
import { getProductsCacheKey } from "../utils/productUtils";

export const PRODUCT_DRAWER_VALIDATION_QUERY_KEY = "productDrawerValidation";

interface UseProductDrawerValidationParams {
	configBundleId: string;
	currentConfiguration: ProductDto[];
	productsToValidate: ProductDto[];
	productIdToReplace?: string | null;
	enabled?: boolean;
}

/**
 * Hook to validate a list of products against the current configuration
 * Used in ProductSelectionDrawer to show validation status for each potential product choice
 */
export function useProductDrawerValidation({
	configBundleId,
	currentConfiguration,
	productsToValidate,
	productIdToReplace,
	enabled = true,
}: UseProductDrawerValidationParams) {
	const configIds = getProductsCacheKey(currentConfiguration);

	const validateIds = getProductsCacheKey(productsToValidate);

	return useQuery<ValidateBatchProductsResponseDto, Error>({
		queryKey: [
			PRODUCT_DRAWER_VALIDATION_QUERY_KEY,
			configBundleId,
			configIds,
			validateIds,
			productIdToReplace,
		],
		queryFn: () => validateBatchProducts(
			currentConfiguration,
			productsToValidate,
			productIdToReplace
		),
		enabled: enabled && productsToValidate.length > 0,
		staleTime: 0,
		gcTime: 2 * 60 * 1000,
		refetchOnMount: true,
	});
}

export function getProductValidationStatus(
	validationResults: ValidateBatchProductsResponseDto | undefined,
	productId: string
): ValidationStatus | undefined {
	if (!validationResults) return undefined;

	const productResult = validationResults.results.find(r => r.productId === productId);

	if (!productResult) return VALIDATION_STATUS.SUCCESS;

	const isRelatedToProduct = (rule: RuleResult) =>
		!rule.relatedProducts || rule.relatedProducts.includes(productId);

	if (productResult.validationResult.errors.some(isRelatedToProduct)) {
		return VALIDATION_STATUS.ERROR;
	}

	if (productResult.validationResult.warnings.some(isRelatedToProduct)) {
		return VALIDATION_STATUS.WARNING;
	}

	return VALIDATION_STATUS.SUCCESS;
}

export function getProductValidationErrors(
	validationResults: ValidateBatchProductsResponseDto | undefined,
	productId: string
): RuleResult[] {
	if (!validationResults) return [];

	const productResult = validationResults.results.find(r => r.productId === productId);

	if (!productResult) return [];

	const isRelatedToProduct = (rule: RuleResult) =>
		!rule.relatedProducts || rule.relatedProducts.includes(productId);

	const errors = productResult.validationResult.errors.filter(isRelatedToProduct);
	const warnings = productResult.validationResult.warnings.filter(isRelatedToProduct);

	return [...errors, ...warnings];
}